// Copyright (C) 2024, Lifefinder Systems International AB

import type { Device } from '../types';

/**
 * Battery low threshold (Volts)
 * NOTE: This should match battery_medium_volts in backend maintenance_settings table
 * Default: 2.7V
 */
const BATTERY_LOW_LIMIT: number = 2.7;

/**
 * Check if device battery is low
 * @param {Object} item - Device object with output.volts property
 * @returns {boolean} True if battery voltage is below threshold
 */
export const isBatteryLow = (item: Partial<Device> | null | undefined): boolean =>
  !!(item && item.output && item.output.volts && item.output.volts < BATTERY_LOW_LIMIT);

/**
 * Get battery threshold value
 * Exported for testing and future dynamic threshold support
 * @returns {number} Battery low threshold in Volts
 */
export const getBatteryLowLimit = (): number => BATTERY_LOW_LIMIT;

/**
 * Calculate alarm age in seconds for a device
 * @param {Object} device - Device object with alarmTime property
 * @returns {number} Age in seconds, or -1 if no alarm time
 */
export const calculateAlarmAgeSeconds = (device: Partial<Device> | null | undefined): number => {
  if (!(device && device.alarmTime)) {
    return -1;
  }
  const now = new Date().getTime();
  return (now - new Date(device.alarmTime).getTime()) / 1000;
};

/**
 * Calculate trace age in seconds for a device
 * @param {Object} device - Device object with traceTime property
 * @returns {number} Age in seconds, or -1 if no trace time
 */
export const calculateTraceAgeSeconds = (device: Partial<Device> | null | undefined): number => {
  if (!(device && device.traceTime)) {
    return -1;
  }
  const now = new Date().getTime();
  return (now - new Date(device.traceTime).getTime()) / 1000;
};

/**
 * Check if device should show as trace
 * @param {Object} device - Device object
 * @returns {boolean} True if device should display as trace
 */
export const showAsTrace = (device: Partial<Device> | null | undefined): boolean => {
  const ageS = calculateTraceAgeSeconds(device);
  if (ageS < 0) return false;

  // Import here to avoid circular dependencies
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { getMaxTraceTimeS } = require('./limits');
  return ageS <= getMaxTraceTimeS();
};

/**
 * Check if device should show as alarm
 * @param {Object} device - Device object
 * @returns {boolean} True if device should display as alarm
 */
export const showAsAlarm = (device: Partial<Device> | null | undefined): boolean => {
  const ageS = calculateAlarmAgeSeconds(device);
  if (ageS < 0) return false;

  // Import here to avoid circular dependencies
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { getMaxAlarmTimeS } = require('./limits');
  return ageS <= getMaxAlarmTimeS();
};

/**
 * Calculate active age in seconds for a device.
 *
 * Uses the top-level `deviceActive` timestamp, which the backend only forwards
 * while the active report is fresh (age < MAX_ACTIVE_AGE_S). This is distinct
 * from the raw `output.deviceActive` flag, which is never cleared by age.
 * @param {Object} device - Device object with deviceActive timestamp
 * @returns {number} Age in seconds, or -1 if no active timestamp
 */
export const calculateActiveAgeSeconds = (device: Partial<Device> | null | undefined): number => {
  if (!(device && device.deviceActive)) {
    return -1;
  }
  const now = new Date().getTime();
  return (now - new Date(device.deviceActive).getTime()) / 1000;
};

/**
 * Check if device should show as active.
 *
 * Requires both the reported active flag (output.deviceActive === 1) and a
 * non-expired active timestamp, so a stale green marker never lingers once the
 * device stops reporting active. Mirrors showAsAlarm's age gating.
 * @param {Object} device - Device object
 * @returns {boolean} True if device should display as active
 */
export const showAsActive = (device: Partial<Device> | null | undefined): boolean => {
  if (device?.output?.deviceActive !== 1) return false;

  const ageS = calculateActiveAgeSeconds(device);
  if (ageS < 0) return false;

  // Import here to avoid circular dependencies
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { getMaxActiveTimeS } = require('./limits');
  return ageS <= getMaxActiveTimeS();
};
